import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  ClipboardCheck,
  DownloadIcon,
  EditIcon,
  HelpCircle,
} from "lucide-react";
import { useQCStandardQuery } from "../api/queries";
import { QCDetailTour, useQCDetailTour } from "../tour/QCDetailTour";
import { QCDetailContent } from "../ui/QCDetailContent";

export function QCDetailPage() {
  const { id } = useParams({ from: "/app/qc/$id" });
  const nav = useNavigate({ from: "/app/qc/$id" });
  const tour = useQCDetailTour();
  const canEdit = useActionPermission(["qc:update"]);
  const canRecord = useActionPermission(["qc_record:create"]);

  const { data: qc, isLoading, error } = useQCStandardQuery(id);

  const handleDownloadPDF = () => {
    nav({ to: "/app/qc/$id/pdf-preview", params: { id } });
  };

  const handleEdit = () => {
    nav({ to: "/app/qc/$id/edit", params: { id } });
  };

  const handleRecord = () => {
    nav({ to: "/app/qc/$id/record", params: { id } });
  };

  const handleBack = () => {
    nav({ to: "/app/qc" });
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (error || !qc) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">
                ບໍ່ພົບມາດຕະຖານ QC
              </h2>
              <p className="mb-4 text-muted-foreground">
                ມາດຕະຖານ QC ທີ່ທ່ານຊອກຫາບໍ່ມີຢູ່ ຫຼື ຖືກລຶບໄປແລ້ວ
              </p>
              <Button onClick={handleBack}>
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                ກັບໄປທີ່ລາຍການ QC
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />

      <Main>
        <QCDetailTour run={tour.run} onCallback={tour.handleJoyrideCallback} />

        <div className="space-y-4">
          <div
            className="flex items-center justify-between gap-4"
            data-tourid="detail-header"
          >
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="font-semibold text-2xl">ລາຍລະອຽດມາດຕະຖານ QC</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="mt-1 text-muted-foreground text-sm">
                ຂໍ້ມູນລາຍລະອຽດຂອງມາດຕະຖານ QC
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleDownloadPDF}
                className="gap-2"
              >
                <DownloadIcon className="h-4 w-4" />
                ເບິ່ງ PDF Preview
              </Button>
              {canRecord && (
                <Button
                  onClick={handleRecord}
                  variant="outline"
                  className="gap-2"
                >
                  <ClipboardCheck className="h-4 w-4" />
                  ບັນທຶກຜົນກວດ QC
                </Button>
              )}
              {canEdit && (
                <Button onClick={handleEdit} className="gap-2">
                  <EditIcon className="h-4 w-4" />
                  ແກ້ໄຂ
                </Button>
              )}
              <Button variant="ghost" onClick={handleBack} className="gap-2">
                <ArrowLeftIcon className="h-4 w-4" />
                ກັບ
              </Button>
            </div>
          </div>

          {/* QC Content */}
          <QCDetailContent qc={qc} />
        </div>
      </Main>
    </>
  );
}
